import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Dual-axis verification surface — T-0037 / E04-F06 (T-0115 wires it).
 *
 * Two orthogonal axes on every register entry:
 *   - `vendor_claim`        ∈ {pending, pass, fail, na}
 *   - `avero_verification`  ∈ {unverified, in_progress, pass, fail}
 *
 * D-21 axis-gate (the load-bearing rule per T-0037 §2):
 *   - `vendor_claim` may be moved by any consultant; an optional
 *     `evidence_link_id` whose source_party === 'vendor' or 'avero' is
 *     accepted, source_party === 'client' is rejected.
 *   - `avero_verification` may be moved with Avero-source evidence OR
 *     without any evidence (consultant judgment).
 *
 * Each PATCH moves exactly one axis. The rollup color is derived on
 * read via the backend rollup table — clients render whatever the API
 * returns.
 */

export const VendorClaimValueSchema = z.enum(['pending', 'pass', 'fail', 'na']);
export type VendorClaimValue = z.infer<typeof VendorClaimValueSchema>;

export const AveroVerificationValueSchema = z.enum(['unverified', 'in_progress', 'pass', 'fail']);
export type AveroVerificationValue = z.infer<typeof AveroVerificationValueSchema>;

export const VerificationAxisSchema = z.enum(['vendor_claim', 'avero_verification']);
export type VerificationAxis = z.infer<typeof VerificationAxisSchema>;

export const RollupColorSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey']);
export type RollupColor = z.infer<typeof RollupColorSchema>;

export const VerificationTransitionSchema = z.object({
  id: z.string().uuid(),
  axis: VerificationAxisSchema,
  from_value: z.string(),
  to_value: z.string(),
  evidence_link_id: z.string().uuid().nullable(),
  actor_id: z.string().uuid(),
  transitioned_at: z.string(),
});
export type VerificationTransition = z.infer<typeof VerificationTransitionSchema>;

export const VerificationDetailSchema = z.object({
  id: z.string().uuid(),
  vendor_claim: VendorClaimValueSchema,
  avero_verification: AveroVerificationValueSchema,
  vendor_claim_set_at: z.string().nullable(),
  avero_verification_set_at: z.string().nullable(),
  expected_date: z.string().nullable(),
  rollup_color: RollupColorSchema,
  history: z.array(VerificationTransitionSchema),
});
export type VerificationDetail = z.infer<typeof VerificationDetailSchema>;

export function getVerification(
  engagementId: string,
  registerType: string,
  entryId: string,
): Promise<VerificationDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}/${encodeURIComponent(entryId)}/verification`,
    VerificationDetailSchema,
  );
}

export interface PatchVerificationInput {
  axis: VerificationAxis;
  value: VendorClaimValue | AveroVerificationValue;
  evidence_link_id?: string | null;
}

export function patchVerification(
  engagementId: string,
  registerType: string,
  entryId: string,
  input: PatchVerificationInput,
  idempotencyKey?: string,
): Promise<VerificationDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}/${encodeURIComponent(entryId)}/verification`,
    VerificationDetailSchema,
    {
      method: 'PATCH',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useVerificationQuery(
  engagementId: string | undefined,
  registerType: string | undefined,
  entryId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['verification', engagementId, registerType, entryId] as const,
    queryFn: () => getVerification(engagementId!, registerType!, entryId!),
    enabled:
      Boolean(engagementId) && Boolean(registerType) && Boolean(entryId) && (opts.enabled ?? true),
  });
}

export function usePatchVerificationMutation(
  engagementId: string | undefined,
  registerType: string | undefined,
  entryId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      idempotencyKey,
    }: {
      input: PatchVerificationInput;
      idempotencyKey?: string;
    }) => patchVerification(engagementId!, registerType!, entryId!, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId && registerType) {
        // The register list/cache also needs to refresh — verification
        // values live on the register entry row too.
        void qc.invalidateQueries({
          queryKey: ['verification', engagementId, registerType, entryId],
        });
        void qc.invalidateQueries({ queryKey: ['registers', engagementId, registerType] });
        // The EDL view derives rollup color from the same axes — keep it
        // in sync.
        void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
      }
    },
  });
}

/**
 * UI-side rollup label per README §6 — the prototype uses 'green',
 * 'yellow_divergent', 'red', 'grey' but the surface in this product
 * talks in posture-chip vocabulary. This helper exists so the verify
 * modal can preview "what the rollup will be after I save".
 */
export function deriveLocalRollup(
  vendorClaim: VendorClaimValue,
  averoVerification: AveroVerificationValue,
): RollupColor {
  if (averoVerification === 'pass' && vendorClaim === 'pass') return 'green';
  if (averoVerification === 'pass' && vendorClaim === 'fail') return 'yellow_divergent';
  if (averoVerification === 'fail' && vendorClaim === 'pass') return 'yellow_divergent';
  if (averoVerification === 'fail' && vendorClaim === 'fail') return 'red';
  // Anything else (unverified / in_progress / pending) → grey.
  return 'grey';
}

export const ROLLUP_LABELS: Record<RollupColor, string> = {
  green: 'Verified pass',
  yellow_divergent: 'Divergent',
  red: 'Failed',
  grey: 'Unverified',
};
